import React, { useState, useEffect } from 'react';
import { Workflow, Play, Pause, Plus, Zap, Clock, Target, Edit } from 'lucide-react';
import { funnelService } from '../../../services/funnelService';

const WorkflowPanel = ({ funnel, userId }) => {
  const [workflows, setWorkflows] = useState([]);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowFormData, setWorkflowFormData] = useState({
    funnel_id: funnel?.id || '',
    name: '',
    description: '',
    trigger_conditions: {},
    actions: [],
    is_active: true,
    user_id: userId
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (funnel?.id) {
      loadWorkflows();
    }
  }, [funnel]);

  const loadWorkflows = async () => {
    if (!funnel?.id) return;
    
    setLoading(true);
    try {
      const { data, error: workflowError } = await funnelService?.getWorkflows(funnel?.id);
      if (workflowError) {
        setError(workflowError);
      } else {
        setWorkflows(data || []);
      }
    } catch (err) {
      setError('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkflow = () => {
    setSelectedWorkflow(null);
    setWorkflowFormData({
      funnel_id: funnel?.id || '',
      name: '',
      description: '',
      trigger_conditions: {
        condition: 'stage_duration',
        value: 7,
        operator: 'greater_than'
      },
      actions: [
        { type: 'send_email', template: 'follow_up_reminder' },
        { type: 'create_task', title: 'Manual follow-up required' }
      ],
      is_active: true,
      user_id: userId
    });
    setShowWorkflowModal(true);
  };

  const handleEditWorkflow = (workflow) => {
    setSelectedWorkflow(workflow);
    setWorkflowFormData({
      ...workflow,
      trigger_conditions: workflow?.trigger_conditions || {},
      actions: workflow?.actions || []
    });
    setShowWorkflowModal(true);
  };

  const handleWorkflowSubmit = async (e) => {
    e?.preventDefault();
    
    if (!workflowFormData?.name?.trim() || !funnel?.id) {
      setError('Please enter workflow name');
      return;
    }

    try {
      const { data, error: submitError } = await funnelService?.createWorkflow({
        ...workflowFormData,
        name: workflowFormData?.name?.trim(),
        trigger_conditions: JSON.stringify(workflowFormData?.trigger_conditions),
        actions: JSON.stringify(workflowFormData?.actions)
      });

      if (submitError) {
        setError(submitError);
      } else {
        setShowWorkflowModal(false);
        setWorkflowFormData({
          funnel_id: funnel?.id || '',
          name: '',
          description: '',
          trigger_conditions: {},
          actions: [],
          is_active: true,
          user_id: userId
        });
        await loadWorkflows();
      }
    } catch (err) {
      setError('Failed to create workflow');
    }
  };

  const toggleWorkflowStatus = async (workflowId, currentStatus) => {
    try {
      const { error: updateError } = await funnelService?.updateWorkflow(workflowId, {
        is_active: !currentStatus
      });

      if (updateError) {
        setError(updateError);
      } else {
        await loadWorkflows();
      }
    } catch (err) {
      setError('Failed to update workflow status');
    }
  };

  const getTriggerDescription = (conditions) => {
    try {
      const parsed = typeof conditions === 'string' ? JSON.parse(conditions) : conditions;
      const { condition, value, operator } = parsed;

      switch (condition) {
        case 'stage_duration':
          return `When lead stays in stage for ${operator === 'greater_than' ? 'more than' : 'exactly'} ${value} days`;
        case 'lead_score':
          return `When lead score is ${operator === 'greater_than' ? 'above' : operator === 'less_than' ? 'below' : 'equal to'} ${value}`;
        case 'no_contact':
          return `When lead has no contact for ${value} days`;
        case 'stage_change':
          return `When lead moves to specific stage`;
        default:
          return 'Custom trigger condition';
      }
    } catch {
      return 'Invalid trigger configuration';
    }
  };

  const getActionsDescription = (actions) => {
    try {
      const parsed = typeof actions === 'string' ? JSON.parse(actions) : actions;
      if (!Array.isArray(parsed) || parsed?.length === 0) return 'No actions configured';
      
      return parsed?.map(action => {
        switch (action?.type) {
          case 'send_email':
            return `Send email (${action?.template})`;
          case 'create_task':
            return `Create task: ${action?.title}`;
          case 'move_stage':
            return `Move to stage: ${action?.stage_name}`;
          case 'assign_user':
            return `Assign to user: ${action?.user_name}`;
          case 'update_score':
            return `Update score by ${action?.score_change}`;
          default:
            return `${action?.type}`;
        }
      })?.join(', ');
    } catch {
      return 'Invalid actions configuration';
    }
  };

  const presetTriggers = [
    {
      name: 'Stage Duration',
      value: {
        condition: 'stage_duration',
        value: 7,
        operator: 'greater_than'
      }
    },
    {
      name: 'High Lead Score',
      value: {
        condition: 'lead_score',
        value: 80,
        operator: 'greater_than'
      }
    },
    {
      name: 'No Contact',
      value: {
        condition: 'no_contact',
        value: 14,
        operator: 'greater_than'
      }
    }
  ];

  const presetActions = [
    {
      name: 'Follow-up Email',
      value: { type: 'send_email', template: 'follow_up_reminder' }
    },
    {
      name: 'Create Task',
      value: { type: 'create_task', title: 'Manual follow-up required' }
    },
    {
      name: 'Score Boost',
      value: { type: 'update_score', score_change: 10 }
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Workflow Automation</h3>
          <p className="text-sm text-gray-600">Automate repetitive tasks and lead nurturing processes</p>
        </div>
        <button
          onClick={handleCreateWorkflow}
          disabled={!funnel}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Workflow
        </button>
      </div>
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button
            onClick={() => setError(null)}
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
          >
            ×
          </button>
        </div>
      )}
      {/* Workflows List */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-4 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-900">
            Active Workflows
            {funnel && (
              <span className="ml-2 text-sm text-gray-500">
                for {funnel?.name}
              </span>
            )}
          </h4>
        </div>

        <div className="p-4">
          {loading && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            </div>
          )}

          <div className="space-y-4">
            {workflows?.map((workflow) => (
              <div key={workflow?.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h5 className="font-medium text-gray-900">{workflow?.name}</h5>
                      <div className="flex items-center space-x-2">
                        <span className={`
                          px-2 py-1 text-xs rounded border
                          ${workflow?.is_active 
                            ? 'bg-green-100 text-green-800 border-green-200' :'bg-gray-100 text-gray-800 border-gray-200'
                          }
                        `}>
                          {workflow?.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {workflow?.execution_count > 0 && (
                          <span className="text-xs text-gray-500">
                            Executed {workflow?.execution_count} times
                          </span>
                        )}
                      </div>
                    </div>

                    {workflow?.description && (
                      <p className="text-sm text-gray-600 mb-3">{workflow?.description}</p>
                    )}

                    <div className="space-y-2">
                      <div className="flex items-start">
                        <Zap className="h-4 w-4 mr-2 mt-0.5 text-yellow-500" />
                        <div>
                          <span className="text-sm font-medium text-gray-700">Trigger: </span>
                          <span className="text-sm text-gray-600">
                            {getTriggerDescription(workflow?.trigger_conditions)}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <Target className="h-4 w-4 mr-2 mt-0.5 text-blue-500" />
                        <div>
                          <span className="text-sm font-medium text-gray-700">Actions: </span>
                          <span className="text-sm text-gray-600">
                            {getActionsDescription(workflow?.actions)}
                          </span>
                        </div>
                      </div>

                      {workflow?.last_executed && (
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-2 text-gray-400" />
                          <span className="text-sm text-gray-500">
                            Last executed: {new Date(workflow.last_executed)?.toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => toggleWorkflowStatus(workflow?.id, workflow?.is_active)}
                      className={`
                        p-2 rounded-md text-sm font-medium
                        ${workflow?.is_active
                          ? 'text-yellow-700 hover:bg-yellow-50' :'text-green-700 hover:bg-green-50'
                        }
                      `}
                      title={workflow?.is_active ? 'Pause workflow' : 'Activate workflow'}
                    >
                      {workflow?.is_active ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </button>

                    <button
                      onClick={() => handleEditWorkflow(workflow)}
                      className="p-2 rounded-md text-gray-700 hover:bg-gray-50"
                      title="Edit workflow"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {workflows?.length === 0 && !loading && (
              <div className="text-center py-12 text-gray-500">
                <Workflow className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h4 className="text-sm font-medium text-gray-900 mb-2">No workflows configured</h4>
                <p className="text-sm text-gray-500 mb-4">
                  {funnel ? 
                    'Create automated workflows to nurture your leads and streamline your sales process.' : 'Select a funnel to create and manage workflows.'
                  }
                </p>
                {funnel && (
                  <button
                    onClick={handleCreateWorkflow}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Workflow
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Workflow Modal */}
      {showWorkflowModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl bg-white rounded-md shadow-lg">
            <form onSubmit={handleWorkflowSubmit}>
              <div className="mt-3">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium text-gray-900">
                    {selectedWorkflow ? 'Edit Workflow' : 'Create New Workflow'}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowWorkflowModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </div>
                
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Workflow Name
                      </label>
                      <input
                        type="text"
                        value={workflowFormData?.name}
                        onChange={(e) => setWorkflowFormData(prev => ({ ...prev, name: e?.target?.value }))}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Enter workflow name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        rows={2}
                        value={workflowFormData?.description}
                        onChange={(e) => setWorkflowFormData(prev => ({ ...prev, description: e?.target?.value }))}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Describe what this workflow does"
                      />
                    </div>
                  </div>

                  {/* Trigger Configuration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Trigger Condition
                    </label>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-2">Choose a preset trigger:</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {presetTriggers?.map((preset) => (
                          <button
                            key={preset?.name}
                            type="button"
                            onClick={() => setWorkflowFormData(prev => ({
                              ...prev,
                              trigger_conditions: preset?.value
                            }))}
                            className="p-3 text-left border border-gray-300 rounded-md hover:bg-blue-50 hover:border-blue-300"
                          >
                            <div className="text-sm font-medium text-gray-900">{preset?.name}</div>
                            <div className="text-xs text-gray-600">{getTriggerDescription(preset?.value)}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Actions Configuration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Actions to Execute
                    </label>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-2">Select actions to perform when triggered:</p>
                      <div className="space-y-2">
                        {presetActions?.map((action) => (
                          <div key={action?.name} className="flex items-center">
                            <input
                              type="checkbox"
                              id={action?.name}
                              checked={workflowFormData?.actions?.some(a => a?.type === action?.value?.type)}
                              onChange={(e) => {
                                if (e?.target?.checked) {
                                  setWorkflowFormData(prev => ({
                                    ...prev,
                                    actions: [...prev?.actions, action?.value]
                                  }));
                                } else {
                                  setWorkflowFormData(prev => ({
                                    ...prev,
                                    actions: prev?.actions?.filter(a => a?.type !== action?.value?.type)
                                  }));
                                }
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor={action?.name} className="ml-2 block text-sm text-gray-900">
                              {action?.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex items-center">
                    <input
                      id="is_active"
                      type="checkbox"
                      checked={workflowFormData?.is_active}
                      onChange={(e) => setWorkflowFormData(prev => ({ ...prev, is_active: e?.target?.checked }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                      Activate workflow immediately
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-8">
                  <button
                    type="button"
                    onClick={() => setShowWorkflowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    {selectedWorkflow ? 'Update Workflow' : 'Create Workflow'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowPanel;